import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, ChevronRight, User, Heart, ShoppingBag, Home, Store, Scissors, Sparkles, Crown, Ribbon, Sun, Droplets, Info } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { supabase } from "@/integrations/supabase/client";
import { getFirstImage } from "@/hooks/useProducts";
import plymhairLogo from "@/assets/plymhair-logo.webp";
import GlossyIcon from "@/components/GlossyIcon";

const navLinks = [
  { label: "Home", href: "/", icon: Home },
  { label: "Shop", href: "/shop", icon: Store },
  { label: "Salon", href: "/salon", icon: Scissors },
  { label: "Wigs", href: "/shop?category=Wigs", icon: Crown },
  { label: "Extensions", href: "/shop?category=Extensions", icon: Sparkles },
  { label: "Hair Pieces", href: "/shop?category=Hair Pieces", icon: Ribbon },
  { label: "Braids", href: "/shop?category=Braids", icon: Scissors },
  { label: "Head Wear", href: "/shop?category=Head Wear", icon: Sun },
  { label: "Hair Care", href: "/shop?category=Hair Care", icon: Droplets },
];

interface NavbarProps {
  transparent?: boolean;
}

/** Morphing dots → lines → X icon */
const MorphingBurger = ({ open, hasNotification, className }: { open: boolean; hasNotification?: boolean; className?: string }) => (
  <div className={`relative w-5 h-4 flex flex-col justify-between ${className}`}>
    <span
      className={`block h-[2px] rounded-full bg-current transition-all duration-400 origin-center ${
        open ? "w-full rotate-45 translate-y-[7px]" : "w-full"
      }`}
    />
    <span
      className={`block h-[2px] rounded-full bg-current transition-all duration-300 ${
        open ? "w-0 opacity-0" : "w-3/4"
      }`}
    />
    <span
      className={`block h-[2px] rounded-full bg-current transition-all duration-400 origin-center ${
        open ? "w-full -rotate-45 -translate-y-[7px]" : "w-1/2"
      }`}
    />
    {/* Pulse ring for notifications */}
    {hasNotification && !open && (
      <>
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full animate-ping opacity-75" />
      </>
    )}
  </div>
);

const Navbar = ({ transparent = false }: NavbarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const { items: cartItems, totalItems, subtotal, setIsOpen } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { recentIds } = useRecentlyViewed();
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const [cartBounce, setCartBounce] = useState(false);
  const prevTotalItems = useRef(totalItems);

  useEffect(() => {
    if (totalItems !== prevTotalItems.current) {
      setCartBounce(true);
      const timer = setTimeout(() => setCartBounce(false), 500);
      prevTotalItems.current = totalItems;
      return () => clearTimeout(timer);
    }
  }, [totalItems]);
  const touchDeltaX = useRef(0);
  const [dragging, setDragging] = useState(false);

  // Recently viewed products for drawer (fetch from supabase)
  const [recentProducts, setRecentProducts] = useState<any[]>([]);
  useEffect(() => {
    if (recentIds.length === 0) { setRecentProducts([]); return; }
    supabase
      .from("products")
      .select("id,name,slug,images,regular_price,sale_price")
      .in("id", recentIds.slice(0, 4))
      .then(({ data }) => {
        if (data) {
          // preserve order
          const map = new Map(data.map((p) => [p.id, p]));
          setRecentProducts(recentIds.slice(0, 4).map((id) => map.get(id)).filter(Boolean));
        }
      });
  }, [recentIds]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!transparent) return;
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [transparent]);

  // Lock body scroll & scale page when mobile menu open
  useEffect(() => {
    const main = document.getElementById("app-main-content");
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      if (main) {
        main.style.transition = "transform 0.5s cubic-bezier(0.22,1,0.36,1), border-radius 0.5s cubic-bezier(0.22,1,0.36,1)";
        main.style.transform = "scale(0.92) translateX(-60px)";
        main.style.borderRadius = "1.5rem";
        main.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = "";
      if (main) {
        main.style.transform = "";
        main.style.borderRadius = "";
        main.style.overflow = "";
      }
    }
    return () => {
      document.body.style.overflow = "";
      if (main) {
        main.style.transform = "";
        main.style.borderRadius = "";
        main.style.overflow = "";
      }
    };
  }, [mobileOpen]);

  // Swipe-to-close gesture
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - touchStartX.current;
    touchDeltaX.current = Math.max(0, delta); // only swipe right
    if (drawerRef.current && touchDeltaX.current > 0) {
      drawerRef.current.style.transform = `translateX(${touchDeltaX.current}px)`;
      drawerRef.current.style.transition = "none";
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    setDragging(false);
    if (drawerRef.current) {
      drawerRef.current.style.transition = "transform 0.3s cubic-bezier(0.22,1,0.36,1)";
      if (touchDeltaX.current > 50) {
        drawerRef.current.style.transform = "translateX(100%)";
        setTimeout(() => {
          setMobileOpen(false);
          if (drawerRef.current) {
            drawerRef.current.style.transform = "";
            drawerRef.current.style.transition = "";
          }
        }, 300);
      } else {
        drawerRef.current.style.transform = "translateX(0)";
        setTimeout(() => {
          if (drawerRef.current) {
            drawerRef.current.style.transform = "";
            drawerRef.current.style.transition = "";
          }
        }, 300);
      }
    }
    touchDeltaX.current = 0;
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setMobileOpen(false);
    }
  };

  const isTransparent = transparent && !scrolled && !mobileOpen;
  const hasNotification = totalItems > 0 || wishlistCount > 0;

  // Stagger delays
  const baseDelay = 50;
  const searchDelay = 0;
  const linkBaseDelay = baseDelay;
  const recentDelay = linkBaseDelay + navLinks.length * 40 + 40;
  const bottomDelay = recentDelay + 80;

  return (
    <header className={`${transparent ? "fixed" : "sticky"} top-0 left-0 right-0 z-50 transition-all duration-300`}>
      <div className="bg-primary h-1.5" />

      <div className={`transition-all duration-300 ${isTransparent ? "bg-transparent" : "bg-background shadow-sm"}`}>
        <div className="container flex items-center gap-1 sm:gap-2 py-2 sm:py-3">
          <Link to="/" className="shrink-0">
            <img
              src={plymhairLogo}
              alt="PlymHair Extensions"
              className={`h-28 sm:h-40 w-auto transition-all duration-500 hover:scale-110 hover:drop-shadow-[0_0_30px_hsl(var(--primary)/0.6)] cursor-pointer ${isTransparent ? "brightness-0 invert hover:brightness-100 hover:invert-0" : "hover:brightness-110"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`nav-link-animated px-2 py-2 text-[11px] font-body font-semibold tracking-widest uppercase transition-colors ${
                  isTransparent
                    ? "text-white/90 hover:text-white"
                    : "text-foreground/80 hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4 ml-auto lg:ml-0 shrink-0">
            <button
              onClick={() => {
                const el = document.getElementById("hero-search-input");
                if (el) el.focus();
                else navigate("/shop");
              }}
              className="hidden sm:flex items-center transition-opacity hover:opacity-80"
              aria-label="Search"
            >
              <GlossyIcon name="search" size={28} className={isTransparent ? "brightness-0 invert" : ""} />
            </button>

            <Link
              to={user ? "/account" : "/auth"}
              aria-label="Account"
              className="hidden sm:flex items-center transition-opacity hover:opacity-80"
            >
              <GlossyIcon name="account" size={28} className={isTransparent ? "brightness-0 invert" : ""} />
            </Link>

            <Link
              to="/wishlist"
              aria-label="Wishlist"
              className="hidden sm:flex items-center transition-opacity hover:opacity-80 relative"
            >
              <GlossyIcon name="heart" size={28} className={isTransparent ? "brightness-0 invert" : ""} />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[9px] font-body font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setIsOpen(true)}
              aria-label="View shopping cart"
              className="flex items-center transition-opacity hover:opacity-80 relative"
            >
              <GlossyIcon name="cart" size={28} className={`${isTransparent ? "brightness-0 invert" : ""} ${cartBounce ? "animate-cart-bounce" : ""}`} />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[9px] font-body font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {totalItems > 99 ? "99+" : totalItems}
                </span>
              )}
            </button>

            <button
              className={`relative p-2.5 transition-colors lg:hidden rounded-full ${isTransparent ? "text-white hover:bg-white/10" : "text-foreground hover:text-primary hover:bg-secondary"}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <MorphingBurger open={mobileOpen} hasNotification={hasNotification} />
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen mobile drawer — portaled to body */}
      {createPortal(
      <div
        className={`fixed inset-0 top-0 z-50 lg:hidden transition-all duration-500 ${
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-foreground/60 backdrop-blur-sm transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />

        {/* Panel — slides from right with swipe support */}
        <div
          ref={drawerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-background shadow-2xl flex flex-col ${
            dragging ? "" : "transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          } ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b border-border/50 ${mobileOpen ? "animate-in fade-in duration-300" : ""}`}
            style={{ animationDelay: `${searchDelay}ms` }}
          >
            <Link to="/" onClick={() => setMobileOpen(false)} className="shrink-0">
              <img src={plymhairLogo} alt="PlymHair" className="h-9 w-auto" />
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              className="w-9 h-9 rounded-full bg-secondary/80 flex items-center justify-center hover:bg-secondary transition-colors"
            >
              <MorphingBurger open={true} className="w-4 h-3" />
            </button>
          </div>

          {/* Search — staggered */}
          <div
            className={`px-5 pt-5 pb-2 ${mobileOpen ? "animate-in fade-in slide-in-from-right-3 fill-mode-both" : ""}`}
            style={{ animationDelay: `${searchDelay + 30}ms`, animationDuration: "300ms" }}
          >
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-3 rounded-full bg-secondary/60 border border-border/50 text-sm font-body placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </form>
          </div>

          {/* Navigation links with icons — staggered */}
          <nav className="flex-1 overflow-y-auto px-3 py-3">
            {navLinks.map((link, i) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 text-sm font-body font-medium tracking-wide hover:text-primary hover:bg-secondary/60 rounded-xl transition-all duration-200 group ${
                    mobileOpen ? "animate-in slide-in-from-right-4 fade-in fill-mode-both" : ""
                  }`}
                  style={{ animationDelay: `${linkBaseDelay + i * 40}ms`, animationDuration: "300ms" }}
                >
                  <div className="w-8 h-8 rounded-lg bg-secondary/80 flex items-center justify-center group-hover:bg-primary/10 transition-colors shrink-0">
                    <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <span className="flex-1">{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200" />
                </Link>
              );
            })}
          </nav>

          {/* Recently viewed thumbnails */}
          {recentProducts.length > 0 && (
            <div
              className={`px-5 py-3 border-t border-border/30 ${mobileOpen ? "animate-in fade-in slide-in-from-bottom-2 fill-mode-both" : ""}`}
              style={{ animationDelay: `${recentDelay}ms`, animationDuration: "300ms" }}
            >
              <p className="text-[10px] font-body font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2.5">Recently Viewed</p>
              <div className="flex gap-2.5">
                {recentProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="w-14 h-14 rounded-xl overflow-hidden bg-secondary/60 shrink-0 border border-border/40 hover:border-primary/40 transition-colors"
                  >
                    <img src={getFirstImage(p.images)} alt={p.name} className="w-full h-full object-cover" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Bottom: mini cart preview + utility actions — staggered */}
          <div
            className={`border-t border-border/50 px-5 py-4 space-y-3 ${mobileOpen ? "animate-in fade-in slide-in-from-bottom-3 fill-mode-both" : ""}`}
            style={{ animationDelay: `${bottomDelay}ms`, animationDuration: "350ms" }}
          >
            {/* Mini cart preview */}
            {cartItems.length > 0 && (
              <button
                onClick={() => { setIsOpen(true); setMobileOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary/50 border border-border/40 hover:border-primary/30 transition-all text-left"
              >
                <div className="flex -space-x-2">
                  {cartItems.slice(0, 3).map((item, i) => (
                    <div
                      key={`${item.product.id}-${item.variantLabel}`}
                      className="w-9 h-9 rounded-lg overflow-hidden border-2 border-background bg-secondary shrink-0"
                      style={{ zIndex: 3 - i }}
                    >
                      <img src={getFirstImage(item.product.images)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {cartItems.length > 3 && (
                    <div className="w-9 h-9 rounded-lg border-2 border-background bg-secondary flex items-center justify-center shrink-0 text-[10px] font-body font-bold text-muted-foreground">
                      +{cartItems.length - 3}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-body font-medium text-foreground">{totalItems} item{totalItems !== 1 ? "s" : ""} in bag</p>
                  <p className="text-[11px] font-body text-muted-foreground">£{subtotal.toFixed(2)}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
              </button>
            )}

            {/* Account */}
            <Link
              to={user ? "/account" : "/auth"}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary/60 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                <User className="w-4 h-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-body font-medium">{user ? "My Account" : "Sign In"}</p>
                <p className="text-[11px] font-body text-muted-foreground">{user ? user.email : "Access your account"}</p>
              </div>
            </Link>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Link
                to="/wishlist"
                onClick={() => setMobileOpen(false)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border/70 hover:border-primary/30 hover:bg-secondary/40 transition-all"
              >
                <Heart className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-body font-medium">Wishlist</span>
                {wishlistCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                    {wishlistCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => { setIsOpen(true); setMobileOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="text-sm font-body font-medium">Bag</span>
                {totalItems > 0 && (
                  <span className="bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>, document.body)}
    </header>
  );
};

export default Navbar;
